import React, { useState, useEffect, useCallback, useRef } from "react";
import api, { baseURL } from "../../utils/api";
import { Link } from "react-router-dom";
import { useAuth } from "../../utils/AuthProvider";
import Payments from "./components/Payments";
import Profile from "./components/Profile";
import SideBar from "../../components/SideBar/SideBar";
import Spinner from "../../components/Spinner/Spinner";
import Header from "./components/HeaderUserdashboard";
import MessageList from "./components/MessageList";
import MessageInput from "./components/MessageInput";
import PredefinedQuestions from "./components/PredefinedQuestions";
import questions from "./utils/questions.json";
import elecCoopQuestions from "./utils/elecCoopQuestions.json";
import { useSendMessage } from "./utils/chatHelper";
import {
  useDeepResearch,
  getDeepResearchHistoryItems,
} from "./utils/deepResearchHelper";
import { countPromptWords } from "./utils/tokenCounter";
import {
  CircleUser,
  FileText,
  LogOut,
  Moon,
  PanelLeftOpen,
  Sparkle,
  Sun,
  Telescope,
  Zap,
  KeyRound,
  Upload,
  X,
  MessageCircle,
} from "lucide-react";
import {
  startDraftBotSession,
  isDraftThreadId,
  deleteEmptyDefaultThreads,
} from "./utils/addThreadActions";
import {
  regroupThreadsByDate,
  getCachedHistory,
  setCachedHistory,
} from "../../components/SideBar/cache/caching";
import { toast } from "sonner";

export default function UserDashboard() {
  const { logout, token, userLevel } = useAuth();
  const [threadId, setThreadId] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalOpenProfile, setModalOpenProfile] = useState(false);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  const [didSearch, setDidSearch] = useState(false);
  const [linkSearch, setLinkSearch] = useState(false);
  const [data, setData] = useState();
  const [wait, setWait] = useState(false);
  const [runId, setRunId] = useState();
  const [messageId, setMessageId] = useState();
  const [eventError, setEventError] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isFetchingSpeech, setIsFetchingSpeech] = useState(false);
  const [settingsModal, setSettingsModal] = useState(false);
  const [speechToPrompt, setSpeechToPrompt] = useState(false);
  const [darkMode, setDarkMode] = useState(false);

  const [thread, setThread] = useState(null);
  const [threads, setThreads] = useState({});
  const [botRole, setBotRole] = useState(null);
  const [error, setError] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [history, setHistory] = useState(getCachedHistory() || {});
  const autoCreatedRef = useRef(false);
  const suppressNextThreadFetchRef = useRef(false);

  const {
    deepResearchLoading,
    deepResearchConversationId,
    setDeepResearchConversationId,
    startNewDeepResearchThread,
    openDeepResearchThread,
    handleDeepResearchSubmit,
    handleDeepResearchRetry,
    removeDeepResearchDraft,
    fetchDeepResearchConversations,
  } = useDeepResearch({
    token,
    threadId,
    setThread,
    setThreadId,
    setBotRole,
    setMessages,
    setNewMessage,
    setSidebarVisible,
    setHistory,
  });

  const dropdownRef = useRef();
  const buttonRef = useRef();
  const user = JSON.parse(sessionStorage.getItem("user"));
  const [profileDropdown, setProfileDropdown] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [announcement, setAnnouncement] = useState(true);
  const [dontShowAnnouncement, setDontShowAnnouncement] = useState(false);
  const responsesApiBots = [
    {
      botId: 1,
      title: "New Chat",
      label: "General Chat",
      Icon: Sparkle,
      buttonClass:
        "text-green-800 hover:bg-green-200 dark:text-green-300 dark:hover:bg-green-900/70",
    },
    {
      botId: 2,
      title: "New Chat",
      label: "Electric Coop Chat",
      Icon: Zap,
      buttonClass:
        "text-yellow-800 hover:bg-yellow-200 dark:text-yellow-300 dark:hover:bg-yellow-900/70",
    },
    {
      botId: 3,
      title: "New Chat",
      label: "Invoicing Checker Chat",
      Icon: FileText,
      buttonClass:
        "text-blue-800 hover:bg-blue-200 dark:text-blue-300 dark:hover:bg-blue-900/70",
    },
  ];

  const startResponsesApiSession = ({ botId, title }) => {
    if (isFetching || wait) return;
    setDeepResearchConversationId(null);
    removeDeepResearchDraft();
    startDraftBotSession({
      botId,
      title,
      setThread,
      setThreadId,
      setBotRole,
      setMessages,
      setNewMessage,
      setHistory,
      setSidebarVisible,
    });
  };
  useEffect(() => {
    const hideAnnouncement = localStorage.getItem("hideAnnouncement");
    if (hideAnnouncement !== "true") {
      setAnnouncement(true);
    }
  }, []);

  const closeAnnouncement = () => {
    if (dontShowAnnouncement) {
      localStorage.setItem("hideAnnouncement", "true");
    }
    setAnnouncement(false);
  };

  const toggleCheckbox = () => {
    setDontShowAnnouncement((prev) => !prev);
  };

  const toggleButton = () => {
    setProfileDropdown((prevState) => !prevState);
  };

  useEffect(() => {
    const savedDarkMode = localStorage.getItem("darkMode") === "true";
    const savedSpeechToPrompt =
      localStorage.getItem("speechToPrompt") === "true";
    setDarkMode(savedDarkMode);
    setSpeechToPrompt(savedSpeechToPrompt);
  }, []);

  // Sync dark mode with the DOM
  useEffect(() => {
    const root = document.documentElement;

    if (darkMode) {
      root.classList.add("dark");
      root.style.colorScheme = "dark";
    } else {
      root.classList.remove("dark");
      root.style.colorScheme = "light";
    }
  }, [darkMode]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 640) {
        setSidebarVisible(true);
      } else {
        setSidebarVisible(false);
      }
    };
    handleResize();
  }, []);

  const fetchMessagesRef = useRef(null);
  const docInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const eventSourceRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    scrollToBottom();
  }, [scrollToBottom]);
  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);
  const closeModalProf = () => setModalOpenProfile(false);
  const openSettings = () => setSettingsModal(!settingsModal);

  useEffect(() => {
    //use to update the thread name
    const fetchThreads = () => {
      const sidebarHistory = JSON.parse(
        localStorage.getItem("sidebar_history") || "{}",
      );
      setThreads(sidebarHistory.data || {});
    };

    fetchThreads();

    const handleStorageChange = (e) => {
      if (e.key === "sidebar_history") {
        fetchThreads();
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const mapResponsesApiConversations = (conversations = []) =>
    conversations.map((conversation) => ({
      id: conversation.id,
      thread_id: conversation.id,
      name: conversation.title || "Responses API Test",
      updated_at: conversation.updated_at,
      last_used:
        conversation.updated_at ||
        conversation.created_at ||
        new Date().toISOString(),
      thread: null,
      bot_role: "responses_api",
      bot_id: conversation.bot_id,
    }));

  const mergeThreadHistoryItems = (...groups) => {
    const byKey = new Map();

    groups.flat().forEach((item) => {
      if (!item) return;
      const key = `${item.bot_role || "none"}:${item.thread_id}`;
      byKey.set(key, item);
    });

    return Array.from(byKey.values());
  };

  const getResponsesApiHistoryItems = (historyData = {}) =>
    Object.values(historyData || {})
      .flat()
      .filter((item) => item?.bot_role === "responses_api");
  const getThreadTimestamp = (item) => {
    const timestamp = item?.updated_at || item?.last_used || item?.created_at;
    const parsed = timestamp ? new Date(timestamp).getTime() : 0;
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const getMostUpdatedThread = (items = []) =>
    [...items]
      .filter(Boolean)
      .sort((a, b) => getThreadTimestamp(b) - getThreadTimestamp(a))[0];

  const activateThread = (item) => {
    if (!item) return;

    setDeepResearchConversationId(null);
    removeDeepResearchDraft();
    setThreadId(item.thread_id);
    setThread(item.thread || null);
    setBotRole(item.bot_role);
    setMessages([]);
    sessionStorage.setItem("threadId", item.thread_id);
    localStorage.setItem("threadId", item.thread_id);
    fetchThreadMessages(item.thread_id, item.bot_role);
  };

  const createGeneralResponsesApiChat = () => {
    if (autoCreatedRef.current) return;
    autoCreatedRef.current = true;
    startDraftBotSession({
      botId: 1,
      title: "New Chat",
      setThread,
      setThreadId,
      setBotRole,
      setMessages,
      setNewMessage,
      setHistory,
      setSidebarVisible,
    });
  };

  const fetchResponsesApiConversations = async () => {
    try {
      const response = await api.get("/v2/responses-chat/conversations", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return mapResponsesApiConversations(response.data || []);
    } catch (error) {
      console.error("Error getting Responses API conversations:", error);
      return [];
    }
  };

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setIsFetching(true);
        setError(null);

        // Check cache first
        const cachedData = getCachedHistory();
        if (cachedData) {
          const [fetchedConversations, deepResearchConversations] =
            await Promise.all([
              fetchResponsesApiConversations(),
              fetchDeepResearchConversations(),
            ]);
          const { survivors: responsesHistoryData, deletedIds } =
            await deleteEmptyDefaultThreads(token, fetchedConversations);
          const cachedResponsesItems = getResponsesApiHistoryItems(
            cachedData,
          ).filter((item) => !deletedIds.includes(item.thread_id?.toString()));
          const cachedDeepResearchItems =
            getDeepResearchHistoryItems(cachedData);
          const mergedHistory = regroupThreadsByDate(
            mergeThreadHistoryItems(
              cachedResponsesItems,
              responsesHistoryData,
              cachedDeepResearchItems,
              deepResearchConversations,
            ),
          );

          setHistory(mergedHistory);
          setCachedHistory(mergedHistory);

          const newestResponsesThread =
            getMostUpdatedThread(responsesHistoryData);
          const isEmpty = Object.values(mergedHistory).every(
            (arr) => arr.length === 0,
          );

          if (newestResponsesThread) {
            activateThread(newestResponsesThread);
          } else if (isEmpty) {
            createGeneralResponsesApiChat(mergedHistory);
          }
          setIsFetching(false);
          return;
        }
        const [fetchedConversations, deepResearchConversations] =
          await Promise.all([
            fetchResponsesApiConversations(),
            fetchDeepResearchConversations(),
          ]);
        const { survivors: responsesHistoryData } =
          await deleteEmptyDefaultThreads(token, fetchedConversations);
        const historyData = mergeThreadHistoryItems(
          responsesHistoryData,
          deepResearchConversations,
        );

        const grouped = regroupThreadsByDate(historyData);

        setHistory(grouped);

        const newestResponsesThread =
          getMostUpdatedThread(responsesHistoryData);
        const isEmpty = Object.values(grouped).every((arr) => arr.length === 0);

        if (newestResponsesThread) {
          activateThread(newestResponsesThread);
        } else if (isEmpty) {
          createGeneralResponsesApiChat(grouped);
        }
        setCachedHistory(grouped);
        setIsFetching(false);
      } catch (error) {
        setError("Error getting history");
        setIsFetching(false);
        console.error("Error getting history:", error);
      }
    };

    fetchHistory();
  }, [token, refresh]);

  useEffect(() => {
    const fetchWordCount = async () => {
      try {
        if (userLevel === "free") {
          const response = await api.get(`/chat/token`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          setWordCount(response.data?.chat_token || 0);
        }
      } catch (error) {
        console.error("Error fetching token:", error);
      }
    };

    if (token && userLevel) {
      fetchWordCount();
    }
  }, [token, userLevel, refresh, isLoading]);

  useEffect(() => {
    const fetchHistory = async (retryCount = 3) => {
      setLoading(true);
      try {
        const responsesHistoryData = await fetchResponsesApiConversations();
        const newestResponsesThread =
          getMostUpdatedThread(responsesHistoryData);

        if (newestResponsesThread) {
          activateThread(newestResponsesThread);
          return;
        }

        createGeneralResponsesApiChat(history);
      } catch (error) {
        console.error("Error getting history:", error);

        if (error.response && error.response.status === 500 && retryCount > 0) {
          setTimeout(() => fetchHistory(retryCount - 1), 3000);
        } else {
          setMessages((prevMessages) => [
            ...prevMessages,
            {
              role: "system",
              text_value:
                "An error occurred while fetching the chat history. Please try again later.",
            },
          ]);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [token]);

  useEffect(() => {
    if (suppressNextThreadFetchRef.current) {
      suppressNextThreadFetchRef.current = false;
      return;
    }
    if (threadId && botRole !== "deep_research" && !isDraftThreadId(threadId)) {
      setLinkSearch(false);
      setData(null);
      fetchThreadMessages(threadId, botRole);
    }
  }, [threadId]);

  const { handleSendMessage } = useSendMessage(
    token,
    userLevel,
    thread,
    threadId,
    botRole,
    setMessages,
    setIsLoading,
    setHasError,
    setWait,
    setDidSearch,
    setLinkSearch,
    setData,
    setNewMessage,
    runId,
    setRunId,
    messageId,
    setMessageId,
    setEventError,
    setRefresh,
    setHistory,
    eventSourceRef,
    setThreadId,
    suppressNextThreadFetchRef,
  );

  const activeThreadItem = Object.values(history || {})
    .flat()
    .find(
      (item) =>
        item?.thread_id?.toString() === threadId?.toString() &&
        item?.bot_role === botRole,
    );
  const activeResponsesBotId =
    botRole === "responses_api" ? Number(activeThreadItem?.bot_id || 1) : null;
  const isDocumentUploadDisabled = activeResponsesBotId === 0;
  const isDocumentFile = (file) =>
    [".pdf", ".docx", ".pptx", ".txt"].some((ext) =>
      file?.name?.toLowerCase().endsWith(ext),
    );

  useEffect(() => {
    if (!isDocumentUploadDisabled) return;

    setSelectedFiles((prev) => prev.filter((file) => !isDocumentFile(file)));
  }, [isDocumentUploadDisabled]);

  const handlePredefinedQuestionClick = (question) => {
    const tokenCount = countPromptWords(question);
    if (
      wordCount + tokenCount <= 5000 ||
      userLevel === "premium" ||
      userLevel === "employee"
    ) {
      handleSendMessage(question, null, "user", tokenCount);
    }
  };

  const handleDocUpload = () => {
    if (isDocumentUploadDisabled) return;
    docInputRef.current.click();
  };

  const handleImageUpload = () => {
    imageInputRef.current.click();
  };

  const handleFileChange = (input) => {
    let files = [];

    if (input?.target?.files) {
      // Called from input onChange
      files = Array.from(input.target.files);
    } else if (Array.isArray(input)) {
      // Called directly with file array
      files = input;
    }
    const allowedFiles = isDocumentUploadDisabled
      ? files.filter((file) => !isDocumentFile(file))
      : files;

    setSelectedFiles((prev) => [...prev, ...allowedFiles]);
  };

  const handleChangePassword = async () => {
    try {
      const response = await api.post(
        `/email/forget-password?email=${user.email}`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (response.status !== 200) {
        toast.error("Error sending email to reset password", {
          style: darkMode
            ? { background: "#15803d", color: "white" }
            : { background: "white", color: "#14532d" },
        });
      }
      toast.success("We have sent you an email to reset your password", {
        style: darkMode
          ? { background: "#15803d", color: "white" }
          : { background: "white", color: "#14532d" },
      });
    } catch (error) {
      toast.error("Error sending email to reset password");
    }
  };

  const normalizeResponsesApiAttachments = (attachments = []) => {
    const images = [];
    const files = [];

    attachments.forEach((attachment) => {
      const filename = attachment?.filename || attachment?.name || "Attachment";
      const mimeType = attachment?.mime_type || attachment?.content_type || "";
      const fileId = attachment?.file_id;

      if (mimeType.startsWith("image/") && fileId) {
        images.push({
          name: filename,
          url: `https://${baseURL}/chat/openai-image/${fileId}`,
          file_id: fileId,
          mime_type: mimeType,
        });
        return;
      }

      if (filename) {
        files.push(filename);
      }
    });

    return {
      image: images,
      file: files,
      filenames: files,
    };
  };

  const normalizeResponsesApiMessages = (items = []) =>
    items.map((item) => ({
      id: item.id,
      role: item.role,
      text_value: item.content?.text || "",
      sources: item.content?.sources || [],
      created_at: item.created_at,
      timestamp: item.created_at,
      ...normalizeResponsesApiAttachments(item.attachments || []),
    }));
  const fetchThreadMessages = useCallback(
    async (threadId, selectedBotRole = botRole) => {
      setThreadId(threadId);
      setThread(thread);
      setBotRole(selectedBotRole);
      setLoading(true);
      setMessages([]);
      setEventError(false);

      const cachedMessages = JSON.parse(
        localStorage.getItem(`messages_${threadId}`),
      );
      if (cachedMessages) {
        setLoading(false);
        setMessages(cachedMessages);
        return;
      }
      try {
        const response = await api.get(
          `/v2/responses-chat/conversation/${threadId}/history`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        const fetchedMessages = normalizeResponsesApiMessages(response.data);
        if (fetchedMessages && fetchedMessages.length > 0) {
          setMessages(fetchedMessages);
          localStorage.setItem(
            `messages_${threadId}`,
            JSON.stringify(fetchedMessages),
          );
        } else {
          setMessages([]);
        }
      } catch (error) {
        console.error("Error fetching messages:", error);
        setMessages([]);
      } finally {
        setLoading(false);
        setIsLoading(false);
      }
    },
    [threadId, botRole, token],
  );

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;

    const droppedFiles = Array.from(e.dataTransfer.files);
    const allowedFiles = isDocumentUploadDisabled
      ? droppedFiles.filter((file) => !isDocumentFile(file))
      : droppedFiles;

    setSelectedFiles((prev) => [...prev, ...allowedFiles]);
  };

  return (
    <div>
      <div className="flex h-screen min-w-0 overflow-hidden text-black">
        {/* Sidebar Container */}
        <div className="relative">
          {sidebarVisible && (
            <div
              className="fixed inset-0 z-20 bg-black bg-opacity-50 sm:hidden"
              onClick={() => setSidebarVisible(true)}
            />
          )}
          {/* Sidebar */}
          <div
            className={`flex flex-col h-screen  ${
              sidebarVisible ? "sm:w-64" : "w-0 sm:w-12"
            } transition-width duration-500`}>
            <div
              className={`h-full w-full dark:bg-slate-900 dark:text-gray-200  bg-slate-200 text-gray-900 transform transition-transform duration-500 z-30 ${
                sidebarVisible
                  ? "translate-x-0"
                  : "-translate-x-full sm:-translate-x-0"
              } sm:relative fixed`}>
              <div className="flex flex-col h-screen">
                {sidebarVisible ? (
                  <SideBar
                    userLevel={userLevel}
                    token={token}
                    history={history}
                    setHistory={setHistory}
                    threadId={threadId}
                    botRole={botRole}
                    setThread={setThread}
                    setThreadId={setThreadId}
                    setBotRole={setBotRole}
                    setMessages={setMessages}
                    logout={logout}
                    sidebarVisible={sidebarVisible}
                    setSidebarVisible={setSidebarVisible}
                    openModal={openModal}
                    wait={wait}
                    fetchThreadMessages={fetchThreadMessages}
                    setLoading={setLoading}
                    loading={loading}
                    darkMode={darkMode}
                    setDarkMode={setDarkMode}
                    handleChangePassword={handleChangePassword}
                    onStartDeepResearch={startNewDeepResearchThread}
                    onOpenDeepResearchThread={openDeepResearchThread}
                    setDeepResearchConversationId={
                      setDeepResearchConversationId
                    }
                    removeDeepResearchDraft={removeDeepResearchDraft}
                  />
                ) : (
                  <div className="flex flex-col h-screen items-center gap-4 pt-2.5">
                    <button
                      className="relative p-1.5 rounded-md dark:text-gray-300 group hover:bg-slate-300 dark:hover:bg-slate-700 hover:text-gray-700 dark:hover:text-gray-300"
                      onClick={() => setSidebarVisible(!sidebarVisible)}>
                      <PanelLeftOpen size={20} />
                      <div className="absolute bottom-0 -right-4 py-0.5 px-2.5 text-sm font-semibold text-gray-200 translate-x-[100%] -translate-y-[20%] bg-slate-500 rounded-md opacity-0 group-hover:opacity-100 pointer-events-none">
                        Dashboard
                      </div>
                    </button>
                    <div className="flex flex-col gap-1">
                      {responsesApiBots.map(
                        ({ botId, title, label, Icon, buttonClass }) => (
                          <button
                            key={botId}
                            className={`relative p-1.5 rounded-md group ${buttonClass}`}
                            onClick={() =>
                              startResponsesApiSession({ botId, title })
                            }>
                            <Icon size={20} />
                            <div className="absolute bottom-0 -right-4 py-0.5 px-2.5 text-sm font-semibold text-gray-200 translate-x-[100%] -translate-y-[20%] bg-slate-500 rounded-md opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap">
                              {label}
                            </div>
                          </button>
                        ),
                      )}
                      <button
                        className="relative p-1.5 rounded-md group text-orange-800 hover:bg-orange-200 dark:text-orange-300 dark:hover:bg-orange-900/70"
                        onClick={startNewDeepResearchThread}>
                        <Telescope size={20} />
                        <div className="absolute bottom-0 -right-4 py-0.5 px-2.5 text-sm font-semibold text-gray-200 translate-x-[100%] -translate-y-[20%] bg-slate-500 rounded-md opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap">
                          Deep Research
                        </div>
                      </button>
                    </div>
                    <div className="flex flex-col items-center gap-2 mt-auto mb-8">
                      <>
                        {darkMode ? (
                          <button className="relative p-1 rounded-md dark:text-gray-100 dark:hover:text-gray-300 group hover:bg-slate-700">
                            <Sun
                              size={20}
                              onClick={() => setDarkMode(false)}
                            />
                            <div className="absolute bottom-0 -right-4 py-0.5 px-2.5 text-sm font-semibold text-gray-200 translate-x-[100%] -translate-y-[10%] bg-slate-500 rounded-md opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap">
                              Light Mode
                            </div>
                          </button>
                        ) : (
                          <button className="relative p-1 text-gray-900 rounded-md hover:text-gray-700 group hover:bg-slate-300">
                            <Moon
                              size={20}
                              onClick={() => setDarkMode(true)}
                            />
                            <div className="absolute bottom-0 -right-4 py-0.5 px-2.5 text-sm font-semibold text-gray-200 translate-x-[100%] -translate-y-[10%] bg-slate-500 rounded-md opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap">
                              Dark Mode
                            </div>
                          </button>
                        )}
                        <div className="w-full h-0.5 bg-slate-400 rounded-full" />
                        <div className="flex items-center gap-2 mt-2">
                          <div
                            className="relative flex items-center justify-center transition-colors duration-100 bg-green-600 border rounded-full cursor-pointer border-slate-800 w-7 h-7 hover:bg-green-700 dark:border-slate-200"
                            onClick={toggleButton}
                            ref={buttonRef}>
                            {user ? user.first_name?.charAt(0) : "A"}
                            {profileDropdown && (
                              <div
                                ref={dropdownRef}
                                className="absolute z-50 w-56 p-2 text-sm font-normal -translate-x-3 rounded shadow-md cursor-default bottom-2 left-2 ring-1 dark:ring-slate-600 ring-slate-300 bg-slate-100 shadow-black/25 -translate-y-1/4 dark:bg-slate-800 ">
                                <div className="flex items-center w-full gap-2 p-2 text-left text-gray-600 dark:text-gray-400">
                                  <CircleUser
                                    className="min-w-4"
                                    size={16}
                                    strokeWidth={2.2}
                                  />
                                  <span className="overflow-hidden text-ellipsis">
                                    {user.email}
                                  </span>
                                </div>
                                <button
                                  className="flex items-center w-full gap-2 p-2 text-sm text-left rounded-lg hover:bg-slate-300 dark:hover:bg-slate-700"
                                  onClick={handleChangePassword}>
                                  <KeyRound
                                    size={16}
                                    strokeWidth={2.2}
                                  />
                                  Change Password
                                </button>
                                <button
                                  onClick={openModal}
                                  className="flex items-center w-full gap-2 p-2 text-sm text-left rounded-lg hover:bg-slate-300 dark:hover:bg-slate-700">
                                  <Sparkle
                                    size={16}
                                    strokeWidth={2.2}
                                  />
                                  Change Plan
                                </button>
                                <button
                                  onClick={logout}
                                  className="flex items-center w-full gap-2 p-2 text-sm text-left text-red-700 rounded-lg hover:bg-slate-300 dark:hover:bg-slate-700 dark:text-red-400">
                                  <LogOut
                                    size={16}
                                    strokeWidth={2.2}
                                  />
                                  Logout
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* Main Content */}
        <div className="relative flex flex-col flex-grow h-screen min-w-0 overflow-x-hidden">
          {/* Header */}
          <div className="sticky top-0 z-20 shadow-sm">
            <Header
              userLevel={userLevel}
              sidebarVisible={sidebarVisible}
              setSidebarVisible={setSidebarVisible}
              dropdownOpen={dropdownOpen}
              setDropdownOpen={setDropdownOpen}
              logout={logout}
              wordCount={wordCount}
              openModal={openModal}
              openSettings={openSettings}
              darkMode={darkMode}
            />
          </div>
          {/* Content Area */}
          <div className="flex flex-col flex-grow h-screen min-w-0 overflow-x-hidden overflow-y-auto">
            <div className="flex flex-grow">
              <div
                className="relative flex-grow h-full min-w-0 p-4 pb-40 overflow-x-hidden overflow-y-auto border-r border-slate-300 bg-slate-50 scrollbar-thin dark:bg-slate-800"
                // style={{ height: "calc(80vh - 64px)" }}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}>
                {isDragging && (
                  <div className="absolute inset-0 z-50 flex items-center justify-center h-full gap-2 text-sm font-semibold text-white border-2 border-green-400 border-dashed rounded-lg bg-slate-800/60 backdrop-blur-sm">
                    <span>Drop your files here</span>
                    <Upload
                      size={16}
                      strokeWidth={2.5}
                    />
                  </div>
                )}
                {loading ? (
                  <Spinner />
                ) : (
                  <>
                    <MessageList
                      messages={messages}
                      setMessages={setMessages}
                      isLoading={isLoading}
                      setIsLoading={setIsLoading}
                      messagesEndRef={messagesEndRef}
                      hasError={hasError}
                      setHasError={setHasError}
                      didSearch={didSearch}
                      setDidSearch={setDidSearch}
                      linkSearch={linkSearch}
                      setLinkSearch={setLinkSearch}
                      data={data}
                      setData={setData}
                      threadId={threadId}
                      token={token}
                      messageId={messageId}
                      setMessageId={setMessageId}
                      setRunId={setRunId}
                      eventSourceRef={eventSourceRef}
                      setWait={setWait}
                      eventError={eventError}
                      botRole={botRole}
                      wait={wait}
                      isRecording={isRecording}
                      setIsRecording={setIsRecording}
                      onRetryDeepResearch={handleDeepResearchRetry}
                      deepResearchLoading={deepResearchLoading}
                      // handleRegenerate={handleRegenerate}
                      // stopStream={stopStream}
                    />
                    {messages.length === 0 && !loading && (
                      <>
                        {botRole === "deep_research" ? (
                          <div className="flex flex-col items-center justify-center gap-3 pt-20 text-center text-orange-700 dark:text-orange-300">
                            <Telescope size={40} />
                            <h2 className="text-xl font-bold">
                              BETH AI Deep Research
                            </h2>
                            <p className="max-w-md text-sm text-gray-600 dark:text-gray-400">
                              Ask a question and BETH will research it in depth,
                              gathering sources and compiling a report. This can
                              take a few minutes.
                            </p>
                          </div>
                        ) : (
                          <PredefinedQuestions
                            activeResponsesBotId={activeResponsesBotId || 1}
                            predefinedQuestions={
                              activeResponsesBotId === 2
                                ? elecCoopQuestions
                                : questions
                            }
                            handlePredefinedQuestionClick={
                              handlePredefinedQuestionClick
                            }
                          />
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="z-20 overflow-visible">
              <MessageInput
                token={token}
                messages={messages}
                userLevel={userLevel}
                newMessage={newMessage}
                setNewMessage={setNewMessage}
                handleSendMessage={handleSendMessage}
                handleFileChange={handleFileChange}
                docInputRef={docInputRef}
                imageInputRef={imageInputRef}
                handleDocUpload={handleDocUpload}
                handleImageUpload={handleImageUpload}
                setModalOpen={setModalOpen}
                messageWordCount={wordCount}
                isLoading={isLoading}
                setIsLoading={setIsLoading}
                selectedFiles={selectedFiles}
                setSelectedFiles={setSelectedFiles}
                wait={wait}
                setWait={setWait}
                runId={runId}
                threadId={threadId}
                setThreadId={setThreadId}
                isRecording={isRecording}
                setIsRecording={setIsRecording}
                isFetchingSpeech={isFetchingSpeech}
                setIsFetchingSpeech={setIsFetchingSpeech}
                speechToPrompt={speechToPrompt}
                setSpeechToPrompt={setSpeechToPrompt}
                eventSourceRef={eventSourceRef}
                setLinkSearch={setLinkSearch}
                setDidSearch={setDidSearch}
                setMessages={setMessages}
                botRole={botRole}
                isDocumentUploadDisabled={isDocumentUploadDisabled}
                setBotRole={setBotRole}
                deepResearchLoading={deepResearchLoading}
                deepResearchAwaitingClarification={
                  deepResearchConversationId !== null
                }
                deepResearchCompleted={
                  botRole === "deep_research" &&
                  deepResearchConversationId === null &&
                  ["completed", "failed", "ready_for_research"].includes(
                    messages[messages.length - 1]?.deepResearch?.status,
                  )
                }
                handleDeepResearchSubmit={handleDeepResearchSubmit}
                onStartDeepResearch={startNewDeepResearchThread}
              />
            </div>
          </div>
        </div>
        <Payments
          userLevel={userLevel}
          modalOpen={modalOpen}
          darkMode={darkMode}
          closeModal={closeModal}
        />
        <Profile
          userLevel={userLevel}
          modalOpen={modalOpenProfile}
          closeModal={closeModalProf}
          logout={logout}
          user={userLevel}
        />
        {/* <Settings
          settingsModal={settingsModal}
          setSettingsModal={setSettingsModal}
          darkMode={darkMode}
          speechToPrompt={speechToPrompt}
          setSpeechToPrompt={setSpeechToPrompt}
          userLevel={userLevel}
        /> */}
        {announcement && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="w-[640px] flex flex-col gap-8 relative items-center py-10 text-center rounded-md shadow-sm shadow-black/30 bg-customSlate-300 ">
              <button
                className="absolute top-0 right-0 p-2 rounded-full text-customText-900 -translate-x-1/3 translate-y-1/3 hover:bg-red-500 hover:text-customText-100"
                onClick={closeAnnouncement}>
                <X className="size-5" />
              </button>
              <div>
                <h2 className="text-2xl font-bold text-customGreen-700">
                  Image Upload Feature Re-enabled
                </h2>
              </div>
              <div className="flex flex-col items-center w-full gap-2 text-sm text-text-900">
                <span className="px-12 ">
                  The issue affecting image attachments in the OpenAI Assistant
                  API has now been resolved.
                </span>
                <span className="px-12 font-bold">
                  You can now upload images and use chat threads with image
                  history as usual.
                </span>
                <span className="px-12 ">
                  Thank you for your patience while we worked on restoring this
                  feature.
                </span>
                <Link
                  to="https://community.openai.com/t/error-when-attaching-images-via-assistants-api/1374062"
                  target="_blank">
                  <button className="flex px-4 mt-4 py-1.5 gap-2.5 w-fit items-center font-semibold rounded-full text-customText-100 bg-customGreen-500 hover:bg-customGreen-700">
                    <MessageCircle className="size-5" />
                    <span>Read Community Discussion</span>
                  </button>
                </Link>
              </div>
              <div className="flex items-center justify-center gap-2">
                <input
                  type="checkbox"
                  id="dontShowAnnounce"
                  checked={dontShowAnnouncement}
                  onChange={toggleCheckbox}
                  className="w-4 h-4 rounded text-customGreen-700 focus:ring-customGreen-500"
                />
                <label
                  htmlFor="dontShow"
                  className="text-sm cursor-pointer text-customText-800">
                  Don’t show this again
                </label>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
